import assertNever from "assert-never";
import React from "react";
import { Page } from "../activities/AppPage";
import { API } from "../API";
import { callNumber } from "../external_link";
import useCaregiver from "../hooks/useCaregiver";
import useMixin from "../hooks/useMixin";
import { isLoading } from "../Loading";
import { AddressComponents, AgencyId } from "../messages";
import { PatientId, VisitBroadcastId, VisitInstanceId } from "../messages/ObjectIds";
import { AppState } from "../system/AppState";
import { AgencyMember } from "../system/AppState/AppAgenciesState";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { Visit } from "../system/AppState/AppVisitsState";
import { AppChatState } from "../system/AppState/ChatState";
import { doNothing } from "../utils/doNothing";
import { HashSet } from "../utils/HashSet";
import AvailableVisitScreen from "./AvailableVisitScreen";
import FinishOnboardingBeforeStaffingModal from "./FinishOnboardingBeforeStaffingModal";
import { isDefined } from "./Generic/IsDefined";
import LoadingScaffold from "./LoadingScaffold";
import DirectionsBottomSheet from "./Patient/components/DirectionsBottomSheet";
import useAvailableVisitEngagement from "./useAvailableVisitEngagement";
import useDirections from "./useDirections";
import { useVisitChatIcon } from "./VisitChatIcon";
import WithAvailableVisitMultiShift from "./WithAvailableVisitMultishift";
import CaregiverEngagementModals from "./CaregiverVisitEngagementModals/CaregiverEngagementModals";
import { AgenciesBlock, convertToAgenciesBlocks } from "./AgenciesList/AgenciesListUtils";
import { List } from "../utils/List";

interface Props {
  appState: AppState;
  api: API;
  visitBroadcastId: VisitBroadcastId;
  onClickChatWithAgency: (patientId: PatientId, visitBrodcastId: VisitBroadcastId) => void;
  onRequestNavigate: (page: Page) => void;
  onClickGoBack: () => void;
}

type AvailableVisitState =
  | {
      type: "Loading";
    }
  | {
      type: "Ready";
      caregiver: AppCaregiver;
      visit: Visit.Available;
      coordinator: AgencyMember;
      countNewPatientChatMessages: number;
      activeAgencies: List<AgenciesBlock>;
    }
  | {
      type: "Error";
    };

export type CheckedVisitData = CheckedSingleVisitData | CheckedPatternVisitData | CheckedAllShifts;
export interface CheckedSingleVisitData {
  type: "Singles";
  visitInstanceId: VisitInstanceId;
}
export interface CheckedPatternVisitData {
  type: "Pattern";
  dayOfWeek: string;
}

export interface CheckedAllShifts {
  type: "AllShifts";
}

const AvailableVisitActivity = (props: Props) => {
  const {
    state: { isPopperOpen },
  } = useVisitChatIcon();

  const mixin = useMixin(props.appState);
  const caregiver = useCaregiver(props.appState);

  const { openLocation, closeLocation, directionsState } = useDirections();

  const handleClickLocation = (location: AddressComponents) => {
    openLocation([location.location.lat, location.location.lng]);
  };

  const handleClickCall = (
    agencyId: AgencyId,
    visitBroadcastId: VisitBroadcastId,
    phoneNumber: string | null
  ) => {
    if (phoneNumber !== null) {
      callNumber(phoneNumber);
    }
    return props.api.recordCallToAgencyClickByVisitId(visitBroadcastId, agencyId);
  };

  const state = ((): AvailableVisitState => {
    if (
      isLoading(caregiver) ||
      isLoading(mixin.appVisitsState) ||
      isLoading(mixin.appAgenciesState)
    ) {
      return { type: "Loading" };
    }

    const visit = mixin.appVisitsState.visits.find((x) => x.id === props.visitBroadcastId);
    if (!isDefined(visit) || visit.type !== "Available") {
      console.log(
        `visit broadcast id ${props.visitBroadcastId} could not be found. redirecting back.`
      );
      return { type: "Error" };
    }

    const coordinator = mixin.appAgenciesState.allAgencyMembers.get(
      visit.publicVisitBroadcastDetails.createdById
    );

    if (!isDefined(coordinator)) {
      console.log(
        `coordinator of visit broadcast id ${props.visitBroadcastId} could not be found. redirecting back.`
      );
      return { type: "Error" };
    }

    const agencies = convertToAgenciesBlocks(mixin.appAgenciesState.agencies).filter(
      (x) => x.status === "ACTIVE"
    );

    const countNewPatientChatMessages = AppChatState.patientNumNewChatMessages(
      mixin.appChatState,
      visit.publicVisitBroadcastDetails.patientId
    );

    return {
      type: "Ready",
      caregiver: caregiver,
      visit: visit,
      coordinator: coordinator,
      countNewPatientChatMessages: countNewPatientChatMessages,
      activeAgencies: agencies,
    };
  })();

  const {
    activeModalProps,
    onboardingModalProps,
    handleClickCancel,
    handleClickReject,
    handleClickRequestFlexible,
    handleClickRequestVisit,
  } = useAvailableVisitEngagement({
    mixin,
    api: props.api,
    appState: props.appState,
    visitBroadcastId: props.visitBroadcastId,
    onClickRequestMoreVisits: props.onClickGoBack,
    onRequestNavigate: props.onRequestNavigate,
    onClickGoBack: props.onClickGoBack,
    visit: state.type === "Ready" ? state.visit : null,
    isCaregiverOfMedflyt: state.type === "Ready" ? state.caregiver.isCaregiverOfMedFlyt : false,
    caregiverLocation: state.type === "Ready" ? state.caregiver.addressGeoLocation : null,
  });

  switch (state.type) {
    case "Loading":
      return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
    case "Ready":
      if (state.visit.publicVisitBroadcastDetails.allowMultipleCaregivers) {
        return (
          <>
            <WithAvailableVisitMultiShift
              visit={state.visit}
              onClickCancel={handleClickCancel}
              onClickRequest={handleClickRequestVisit}
            >
              {(multiShiftProps) => (
                <AvailableVisitScreen
                  type="AvailableVisit"
                  {...multiShiftProps}
                  visit={state.visit}
                  engagementStatus={getVisitEngagementStatus(state.visit)}
                  caregiver={state.caregiver}
                  coordinator={state.coordinator}
                  isPopperOpen={isPopperOpen}
                  countNewPatientChatMessages={state.countNewPatientChatMessages}
                  onClickLocation={handleClickLocation}
                  onClickGoBack={props.onClickGoBack}
                  onClickChat={props.onClickChatWithAgency}
                  onClickAvailableVisitCallAgency={handleClickCall}
                  onClickReject={handleClickReject}
                  onClickRequestFlexible={handleClickRequestFlexible}
                  activeAgencies={state.activeAgencies}
                />
              )}
            </WithAvailableVisitMultiShift>
            <DirectionsBottomSheet {...directionsState} onRequestClose={closeLocation} />
            <CaregiverEngagementModals modalProps={activeModalProps} />
            <FinishOnboardingBeforeStaffingModal {...onboardingModalProps} />
          </>
        );
      } else {
        const selectedInstances =
          state.visit.publicVisitBroadcastDetails.type !== "FlexiblePattern"
            ? state.visit.publicVisitBroadcastDetails.visitInstances.map((x) => x.visitInstanceId)
            : [];

        const selectedPatterns =
          state.visit.publicVisitBroadcastDetails.type === "Pattern"
            ? [...Object.keys(state.visit.publicVisitBroadcastDetails.weeklyPattern.daysOfWeek)]
            : [];

        return (
          <>
            <AvailableVisitScreen
              type="AvailableVisit"
              areAllShiftsChecked={true}
              checkedPatterns={HashSet.fromCollection(selectedPatterns)}
              checkedVisits={HashSet.fromCollection(selectedInstances)}
              onCheckVisit={doNothing}
              onClickCancel={() => handleClickCancel(selectedInstances)}
              onClickRequest={handleClickRequestVisit}
              visit={state.visit}
              engagementStatus={getVisitEngagementStatus(state.visit)}
              caregiver={state.caregiver}
              coordinator={state.coordinator}
              isPopperOpen={isPopperOpen}
              countNewPatientChatMessages={state.countNewPatientChatMessages}
              onClickLocation={handleClickLocation}
              onClickGoBack={props.onClickGoBack}
              onClickChat={props.onClickChatWithAgency}
              onClickAvailableVisitCallAgency={handleClickCall}
              onClickReject={handleClickReject}
              onClickRequestFlexible={handleClickRequestFlexible}
              activeAgencies={state.activeAgencies}
            />
            <DirectionsBottomSheet {...directionsState} onRequestClose={closeLocation} />
            <CaregiverEngagementModals modalProps={activeModalProps} />
            <FinishOnboardingBeforeStaffingModal {...onboardingModalProps} />
          </>
        );
      }
    case "Error":
      return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
    default:
      assertNever(state);
  }
};

export default AvailableVisitActivity;

function getVisitEngagementStatus(visit: Visit) {
  if (
    visit.type === "Available" &&
    isDefined(visit.caregiverVisitEngagementDetails.pendingConfirmationUntil)
  ) {
    return "PENDING_CONFIRMATION" as const;
  } else if (visit.caregiverVisitEngagementDetails.requested) {
    return "REQUESTED" as const;
  }

  return "AVAILABLE" as const;
}
